import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all"); // all | draft | published
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/posts");
      setPosts(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this piece permanently?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/posts/${id}`);
      setPosts((p) => p.filter((x) => x.id !== id));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) {
    return (
      <p className="font-display italic text-[#5C554E] text-center py-24">
        Lighting the lamps…
      </p>
    );
  }

  const filtered = posts.filter((p) => filter === "all" || p.status === filter);
  const counts = {
    all: posts.length,
    draft: posts.filter((p) => p.status === "draft").length,
    published: posts.filter((p) => p.status === "published").length,
  };

  return (
    <div className="paper-grain min-h-[70vh]" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-12 pb-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-b border-[#1A1A1A] pb-8">
          <div>
            <p className="overline">The Author’s Desk</p>
            <h1 className="font-display text-5xl md:text-6xl tracking-tight text-[#1A1A1A] mt-2">
              Manuscripts
            </h1>
            <p className="mt-3 text-[#5C554E] italic">
              Signed in as <span className="font-medium">{user.email}</span>
            </p>
          </div>
          <Link to="/admin/new" className="btn-solid" data-testid="admin-new-post-button">
            + New Piece
          </Link>
        </div>

        <div className="flex flex-wrap gap-2 mt-8" data-testid="admin-filter-bar">
          {["all", "draft", "published"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className="tag-pill"
              data-active={filter === f}
              data-testid={`admin-filter-${f}`}
            >
              {f} <span className="ml-2 opacity-60">{counts[f]}</span>
            </button>
          ))}
        </div>

        <div className="mt-6" data-testid="admin-post-table">
          {filtered.length === 0 ? (
            <div className="py-16 text-center" data-testid="admin-empty-state">
              <p className="font-display italic text-2xl text-[#5C554E]">
                Nothing here yet — the blank page awaits.
              </p>
              <Link to="/admin/new" className="btn-ghost mt-6 inline-block">
                Begin Writing
              </Link>
            </div>
          ) : (
            filtered.map((p) => (
              <div
                key={p.id}
                className="grid grid-cols-12 gap-4 py-6 border-b border-[#D9D2C7] items-center"
                data-testid={`admin-post-row-${p.id}`}
              >
                <div className="col-span-12 md:col-span-7">
                  <p className="overline">
                    {p.type} · {p.status}
                  </p>
                  <Link
                    to={`/admin/edit/${p.id}`}
                    className="font-display text-2xl text-[#1A1A1A] hover:text-[#C45543] block mt-1"
                    data-testid={`admin-post-title-${p.id}`}
                  >
                    {p.title}
                  </Link>
                  {p.tags?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {p.tags.map((t) => (
                        <span key={t} className="tag-pill">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="col-span-6 md:col-span-3 font-ui text-xs text-[#8B827A]">
                  Updated{" "}
                  {new Date(p.updated_at).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </div>
                <div className="col-span-6 md:col-span-2 flex justify-end gap-3">
                  <Link
                    to={`/admin/edit/${p.id}`}
                    className="btn-link"
                    data-testid={`admin-edit-${p.id}`}
                  >
                    Edit
                  </Link>
                  <button
                    onClick={() => onDelete(p.id)}
                    disabled={busy}
                    className="btn-link"
                    style={{ color: "#C45543" }}
                    data-testid={`admin-delete-${p.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
